import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import TomSelect from "@lib/custom-tom-select"
import { closeModalAndVisitPath, showAlert } from "@lib/utils"
import { post } from "@lib/requests"

export default class extends Controller {

  static values = {
    url: String,
    placeholder: { type: String, default: "Rechercher..." },
    empty: { type: Boolean, default: false },
    openOnFocus: { type: Boolean, default: true },
    labelField: { type: String, default: "name" },
    searchField: { type: String },
    followPath: { type: Boolean, default: true },
    createPath: { type: String, default: "" },
    searchKey: { type: String, default: "q" },
    extraParams: { type: Object, default: {} },
  }

  connect() {

    this.selectOptions = {
      maxItems: this.element.hasAttribute("multiple") ? null : 1,
      // load: this.load.bind(this),
      placeholder: this.placeholderValue,
      labelField: this.labelFieldValue,
      valueField: "id",
      searchField: this.hasUrlValue ? [] : this.searchFieldValue || this.labelFieldValue, // If we have a url, we don't want TomSelect to filter down our options as will be done by the backend
      allowEmptyOption: this.emptyValue,
      onItemAdd: this.onSelect.bind(this),
      openOnFocus: this.openOnFocusValue,
      // create: this.hasCreatePathValue // if it has a create path, we want to create a new function taking the input posting to the path and then setting the option, else should be false
      // The below needs to conformt this this format
      // create: function(input){
        // return { value: input, text: input }
      // },
      create: (this.hasCreatePathValue && this.createPathValue) ? this.createNewItem.bind(this) : false,
      shouldLoad: function(query) {
        return true;  // Always trigger the load function (so we can clear the options on blank queries)
      }
    }

    if (this.hasUrlValue) {
      this.selectOptions.load = this.load.bind(this)
    }

    this.select = new TomSelect(this.element, this.selectOptions)

  }

   /**
   * TomSelect expects (input, callback)
   * Call callback({ id, [labelField]: text, ... }) when ready
   */
   async createNewItem(input, callback) {
    try {
      const response = await post(this.createPathValue, { body: { name: input } })
      if (!response.ok) throw new Error(`Create failed with status ${response.status}`)
      const data = await response.json

      // Build the option using your configured labelField (defaults to "name")
      const option = {
        id: data.id,
        [this.labelFieldValue]: data[this.labelFieldValue] ?? data.name ?? input,
        // Optional extras that won’t hurt:
        text: data[this.labelFieldValue] ?? data.name ?? input, // keeps parity with TomSelect’s default shape
        value: data.id
      }

      callback(option) // ✅ tell TomSelect to add/select this new option
      showAlert("Nouvelle option créée", { type: "success" })
    } catch (error) {
      console.error(error)
      callback() // ✅ signal failure to TomSelect (no option created)
    }
  }

  refresh() {
    this.select && this.select.refreshOptions()
  }

  async load(query, callback) {
    this.select.clearSelect()
    if (!query.trim()) {
      callback([])
      return
    }
    const params = new URLSearchParams({ [this.searchKeyValue]: query, ...this.extraParamsValue })
    const queryUrl = `${this.urlValue}?${params}`
    const response = await fetch(queryUrl, { headers: { accept: "application/json" } })
    const results = await response.json()
    callback(results || [])
  }

  submit() {
    this.element.closest("form").requestSubmit()
  }

  disconnect() {
    this.select && this.select.destroy()
  }

  onSelect(value, item) {
    const selectedItem = this.select.options[value]

    // Clear the search field
    this.select.setTextboxValue("")

    // Emit a custom event here
    const event = new CustomEvent("select:change", { detail: { value, item: selectedItem } })
    this.element.dispatchEvent(event)

    if (selectedItem?.path && this.followPathValue) {
      closeModalAndVisitPath(this, selectedItem.path)
    }
  }

}
